/* eslint-disable no-unused-vars */
import { useEffect, useState, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CssBaseline from "@mui/material/CssBaseline";
import routes from "routes";
import ProtectedRoute from "components/ProtectedRoute";
import PersistLogin from "components/PersistLogin";
import BoxLoading from "components/BoxLoading/boxLoading";
import NotFoundPage from "components/Page404/NotFoundPage";
import Configurator from "components/Configurator";
import GetUser from "components/getUser";
import Sidenav from "components/Sidenav";
import { setMiniSidenav, useMaterialUIController } from "context";
import brandDark from "assets/images/logo-ct.png";
import brandWhite from "assets/images/logo-ct.png";
import { ThemeProvider } from "@mui/material/styles";
import themeLight from "assets/theme";
import themeDark from "assets/theme-dark";
import { Font } from "@react-pdf/renderer";
import RobotoRegular from "assets/Fonts/Roboto/Roboto-Regular.ttf";
import { API_ROUTES } from "utils/constants";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  SettingState,
  StatusDuAnState,
  StatusKhuCumState,
  AlertState,
} from "states";
import CustomAlert from "components/CustomAlert";
import axios from "axios";
import { defaultSettingData } from "utils/constants";
// Register the font outside the component
Font.register({
  family: "RobotoRegular",
  src: RobotoRegular,
});

const App = () => {
  const { t } = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    sidenavColor,
    homePage,
    whiteSidenav,
    transparentSidenav,
    darkMode,
    layout,
    anotherPage,
  } = controller;
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const setStatusProject = useSetRecoilState(StatusDuAnState);
  const setStatusKhuCum = useSetRecoilState(StatusKhuCumState);
  const [alert, setAlert] = useRecoilState(AlertState);
  const setOptionSetting = useSetRecoilState(SettingState);

  const getRoutes = (allRoutes, parentPath = "") => {
    return allRoutes.flatMap((route, index) => {
      if (route.route) {
        const fullPath = parentPath + route.route;
        return (
          <Route
            exact
            path={fullPath}
            element={
              <ProtectedRoute access={route.access} permission={route.key}>
                {route.component}
              </ProtectedRoute>
            }
            key={"route_" + route.key + "_" + index}
          />
        );
      }
      if (route.collapse) {
        // Tạo route cho parent nếu có
        const parentRoute = route.route ? (
          <Route
            exact
            path={parentPath + route.route}
            element={
              <ProtectedRoute access={route.access} permission={route.key}>
                {route.component}
              </ProtectedRoute>
            }
            key={"parent_" + route.key + "_" + index}
          />
        ) : null;

        // Đệ quy để xử lý các route con trong collapse
        const childRoutes = getRoutes(
          route.collapse,
          parentPath + (route.route || "")
        );
        return [parentRoute, ...childRoutes];
      }
      return null;
    });
  };

  const splitRoutes = (routes) => {
    const publicRoutes = [];
    const protectedRoutes = [];

    routes.forEach((route) => {
      if (route.access === 0 || route.access === 3) {
        publicRoutes.push(route);
      } else {
        protectedRoutes.push(route);
      }
      if (route.collapse) {
        const { publicRoutes: childPublic, protectedRoutes: childProtected } =
          splitRoutes(route.collapse);
        publicRoutes.push(...childPublic);
        protectedRoutes.push(...childProtected);
      }
    });

    return { publicRoutes, protectedRoutes };
  };

  const { publicRoutes, protectedRoutes } = splitRoutes(routes);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const getAllStattusProject = async () => {
    try {
      const response = await axios.get(API_ROUTES.TRANGTHAI);
      let result = {};
      response.data?.forEach((item) => {
        result[item?.id] = item;
      });
      setStatusProject(result);
    } catch (error) {
      if (error.response && error.response.status === 500) {
        setAlert({
          alert: true,
          message: "Đã xảy ra lỗi 500. Vui lòng thử lại sau.",
          type: "error",
        });
      } else {
        console.error(error.response);
      }
      return;
    }
  };
  const getAllStattusKhuCumCongNghiep = async () => {
    try {
      const response = await axios.get(API_ROUTES.TRANGTHAIKHUCUM);
      let result = {};
      response.data?.forEach((item) => {
        result[item?.id] = item;
      });
      setStatusKhuCum(result);
    } catch (error) {
      if (error.response && error.response.status === 500) {
        setAlert({
          alert: true,
          message: "Đã xảy ra lỗi 500. Vui lòng thử lại sau.",
          type: "error",
        });
      } else {
        console.error(error.response);
      }
      return;
    }
  };
  const getAllDataSetting = async () => {
    try {
      const response = await axios.get(API_ROUTES.SETTING + "/1");
      const data = response?.data || {};

      // Chuyển đổi pointCenter từ chuỗi sang mảng [lng, lat]
      let pointCenter = defaultSettingData.pointCenter;
      if (data.pointCenter && typeof data.pointCenter === "string") {
        const coords = data.pointCenter
          .replace(/[\[\]]/g, "")
          .split(",")
          .map((coord) => parseFloat(coord.trim()));
        if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
          pointCenter = coords;
        }
      }
      setOptionSetting({
        ...defaultSettingData,
        ...data,
        pointCenter,
      });
    } catch (error) {
      if (error.response && error.response.status === 500) {
        setAlert({
          alert: true,
          message: "Đã xảy ra lỗi 500. Vui lòng thử lại sau.",
          type: "error",
        });
      }
      setOptionSetting(defaultSettingData);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([
          getAllDataSetting(),
          getAllStattusProject(),
          getAllStattusKhuCumCongNghiep(),
        ]);
        setIsLoadingSettings(false);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        setIsLoadingSettings(false);
        if (error.response && error.response.status === 500) {
          setAlert({
            alert: true,
            message: "Đã xảy ra lỗi 500. Vui lòng thử lại sau.",
            type: "error",
          });
        }
      }
    };
    fetchInitialData();
  }, []);

  const handleCloseAlert = () => {
    setAlert({ alert: false, message: null, type: null });
  };

  if (isLoadingSettings) {
    return <BoxLoading />;
  }

  return (
    <ThemeProvider theme={darkMode ? themeDark : themeLight}>
      <CssBaseline />
      <CustomAlert alert={alert} onClose={handleCloseAlert} />
      {!homePage && !anotherPage && layout !== "page" && (
        <>
          <GetUser />
          <Sidenav
            color={sidenavColor}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav
                ? brandDark
                : brandWhite
            }
            // brandName={t("brandName")}
            brandName={t("brandNameNew")}
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
        </>
      )}
      <Suspense fallback={<BoxLoading />}>
        <Routes>
          {getRoutes(publicRoutes)}
          <Route element={<PersistLogin />}>{getRoutes(protectedRoutes)}</Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
};

export default App;
